import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { WordMark } from "@/components/ui/WordMark";
import { cn } from "@/lib/cn";
import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

export type NavEntry = {
  label: string;
  to: string;
  end?: boolean;
};

type Props = {
  entries: NavEntry[];
  homePath: string;
  trailing?: React.ReactNode;
};

export function Navigation({ entries, homePath, trailing }: Props) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // location.key ticks on every navigation including hash-only changes
  // (e.g. /terms#refunds while already on /terms), so the drawer closes
  // even when pathname is unchanged.
  // biome-ignore lint/correctness/useExhaustiveDependencies: location.key is a trigger, not a read
  useEffect(() => {
    setOpen(false);
  }, [location.key]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className={cn(
        "sticky top-0 z-40 w-full transition-all duration-300",
        scrolled
          ? "backdrop-blur-md bg-canvas/75 border-b border-line/60"
          : "bg-transparent border-b border-transparent",
      )}
    >
      <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between gap-4 px-5 sm:px-6 lg:px-8">
        <Link to={homePath} className="focus-ring rounded-md" aria-label="Mobeen home">
          <WordMark />
        </Link>

        <nav className="hidden md:flex items-center gap-1.5">
          {entries.map((entry) => (
            <NavLink
              key={entry.to}
              to={entry.to}
              end={entry.end}
              className={({ isActive }) =>
                cn(
                  "relative rounded-full px-3.5 py-2 text-sm font-medium transition-colors",
                  isActive ? "text-ink" : "text-muted hover:text-ink",
                )
              }
            >
              {({ isActive }) => (
                <>
                  {entry.label}
                  <span
                    className={cn(
                      "pointer-events-none absolute inset-x-3 -bottom-px h-px origin-center bg-accent transition-transform duration-300",
                      isActive ? "scale-x-100" : "scale-x-0",
                    )}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {trailing}
          <ThemeToggle />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden grid h-10 w-10 place-items-center rounded-full border border-line/70 bg-surface-raised/60 text-ink focus-ring"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? (
              <X className="h-5 w-5" strokeWidth={1.5} />
            ) : (
              <Menu className="h-5 w-5" strokeWidth={1.5} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={cn(
          "md:hidden overflow-hidden border-t border-line/60 bg-canvas/95 backdrop-blur transition-[max-height,opacity] duration-300",
          open ? "max-h-[60vh] opacity-100" : "max-h-0 opacity-0",
        )}
      >
        <nav className="mx-auto flex max-w-6xl flex-col gap-1 px-5 py-4 sm:px-6">
          {entries.map((entry) => (
            <NavLink
              key={entry.to}
              to={entry.to}
              end={entry.end}
              className={({ isActive }) =>
                cn(
                  "rounded-xl px-4 py-3 text-[15px] font-medium transition-colors",
                  isActive
                    ? "bg-accent-soft text-accent-ink"
                    : "text-muted hover:bg-ink/5 hover:text-ink dark:hover:bg-white/5",
                )
              }
            >
              {entry.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  );
}
